import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import {
  SETTINGS_ACTIVITY_NAME_FIELD,
  SETTINGS_ACTIVITY_DESCRIPTION_FIELD,
  SETTINGS_ACTIVITY_ACTIVE_FIELD
} from '../../actions/types';

import {
  fetchAllActivitiesAdmin,
  createActivity,
  deleteActivity
} from '../../api/activities';

import { useIsMountedRef } from '../wizards-manager/custom-hooks/useIsMountedRef';
import TablePanelSpinner from '../tables-manager/shared/TablePanelSpinner';

import ActivityEditableCell from './ActivityEditableCell';
import ActivityActiveCell from './ActivityActiveCell';
import ActivityActionCell from './ActivityActionCell';
import ActivitySettingsTable from './ActivitySettingsTable';

import './ActivitySettings.scss';

const activityColumns = [
  {
    Header: 'Name',
    accessor: SETTINGS_ACTIVITY_NAME_FIELD,
    Cell: ActivityEditableCell,
    width: 110
  },
  {
    Header: 'Description',
    accessor: SETTINGS_ACTIVITY_DESCRIPTION_FIELD,
    Cell: ActivityEditableCell,
    width: 150
  },
  {
    Header: 'Status',
    accessor: SETTINGS_ACTIVITY_ACTIVE_FIELD,
    Cell: ActivityActiveCell,
    width: 100
  },
  {
    Header: 'Actions',
    id: 'action',
    width: 80,
    Cell: ActivityActionCell
  }
];

const ActivitySettings = () => {
  const isMounted = useIsMountedRef();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    setLoading(true);

    (async () => {
      try {
        const res = await fetchAllActivitiesAdmin();

        if (isMounted.current) {
          setItems(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (isMounted.current) {
          setLoading(false);
          toast.error('Failed to load activities', {
            position: toast.POSITION.BOTTOM_CENTER
          });
        }
      }
    })();
  }, [isMounted]);

  const updateItemsFieldFromCell = useCallback((rowIndex, columnId, value) => {
    setItems((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return { ...row, [columnId]: value };
        }
        return row;
      })
    );
  }, []);

  const updateItemsFromCell = useCallback((rowIndex, columnId, value) => {
    setItems((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return { ...row, ...value };
        }
        return row;
      })
    );
  }, []);

  const handleDeleteItem = useCallback(async (id) => {
    try {
      await deleteActivity(id);
      setItems((old) =>
        old.map((row) =>
          row._id === id ? { ...row, active: false } : row
        )
      );
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const handleCreateItem = useCallback(async (data) => {
    try {
      const res = await createActivity(data);

      if (res.data) {
        setItems((old) => [res.data, ...old]);
        return res.data;
      }

      return null;
    } catch (e) {
      toast.error(
        e.response?.data?.message || 'Failed to create activity',
        { position: toast.POSITION.BOTTOM_CENTER }
      );
      return null;
    }
  }, []);

  return (
    <div className="activity-settings settings-table">
      {loading ? (
        <TablePanelSpinner />
      ) : (
        <ActivitySettingsTable
          items={items}
          fields={activityColumns}
          updateDataFieldFromCell={updateItemsFieldFromCell}
          updateDataFromCell={updateItemsFromCell}
          handelDeleteItem={handleDeleteItem}
          handleCreateItem={handleCreateItem}
        />
      )}
    </div>
  );
};

export default ActivitySettings;
